import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { handleException } from 'clients/exception-handling'
import { useApiClient } from 'clients/yogi-api'
import {
  type AgentEvent,
  useAgentEventSubscription,
} from 'features/trend-detection/hooks/use-agent-events-stream'

/**
 * App-wide agent-run completion/failure toasts, driven by the agent-events SSE
 * stream. Mounted ONCE, app-wide, alongside `useAgentEventsStream` in
 * `ProjectLayout` — it replaces the former 30s status poll. Because the single
 * connection lives in `ProjectLayout`, a run that finishes after the launching
 * tab closed still toasts in any tab opened later.
 *
 * Exactly one toast per terminal run: the single mount rules out a double
 * mount, and a `run_uuid` dedupe set guards a reconnect re-delivering the same
 * terminal event. The set resets on project switch.
 *
 * The event now carries `agent_name`, so issue toasts fire with no follow-up
 * request. Benchmarking completions still one-shot-fetch the run to read
 * `status_message` — the `no_data` vs success distinction the event does not
 * carry (event-triggered, not a poll).
 */
export function useAgentRunToasts(projectId: string | null | undefined): void {
  const apiClient = useApiClient()
  const toastedRunsRef = useRef<Set<string>>(new Set())

  // Reset the dedupe set on project switch so a run_uuid seen under a previous
  // project can't suppress a toast in the new one.
  useEffect(() => {
    toastedRunsRef.current = new Set()
  }, [projectId])

  const handleEvent = useCallback(
    (event: AgentEvent): void => {
      if (event.status !== 'completed' && event.status !== 'failed') return
      if (!projectId) return
      // Mark before any async work so two rapid deliveries of the same terminal
      // event (e.g. a reconnect re-read) fire exactly one toast.
      if (toastedRunsRef.current.has(event.run_uuid)) return
      toastedRunsRef.current.add(event.run_uuid)

      const name = event.agent_name

      if (event.status === 'failed') {
        toast.error(`${name} run failed.`)
        return
      }

      if (event.agent_kind === 'issue') {
        toast.success(
          `${name} finished running — check the Insights Feed for new results.`,
        )
        return
      }

      // Benchmarking completed: resolve status_message for the no_data copy.
      void (async () => {
        try {
          const { data } = await apiClient.get(
            `/benchmarking/runs/${event.run_uuid}?proj_uuid=${projectId}`,
          )
          const { status_message } = data as { status_message: string | null }
          if (status_message === 'no_data') {
            toast.error(
              `${name}: No matching data was found for the configured filters. Double check that your filters have data for them.`,
              { autoClose: 10000 },
            )
            return
          }
        } catch (cause) {
          handleException(cause, {
            operation: 'agent.run.completion-details.load',
            disposition: 'recovered',
            reason:
              'Completed run still receives the generic completion message.',
          })

          // Transient fetch failure: fall through to the generic success copy so
          // a completed benchmarking run still surfaces a toast.
        }
        toast.success(`${name} analysis complete! New insights are available.`)
      })()
    },
    [projectId, apiClient],
  )

  useAgentEventSubscription(handleEvent)
}
